import { useCallback, useEffect, useReducer, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiError, type Translator } from '../../../lib/api/error.js';
import { WALLET_QUERY_KEYS } from '../../wallet/api.js';
import { invalidateClaimState } from '../../promo/api.js';
import { cashoutMines, getActiveMines, openMines, revealMines } from './api.js';
import type { MinesActiveState, MinesCashoutResult, MinesLiveState, MinesRevealResult, MinesUnconfirmedState } from './types.js';

/** `confirming`: the bet was sent and the wallet has not confirmed it yet (an unconfirmed stake). */
export type MinesStatus = 'idle' | 'confirming' | 'active' | 'busted' | 'cashed';

/** How often a confirming round asks `/active` whether its bet was confirmed. */
export const CONFIRM_POLL_MS = 3_000;

export interface MinesState {
  status: MinesStatus;
  roundId: string | null;
  minesCount: number;
  bet: string;
  currency: string;
  revealedTiles: number[];
  multiplierBp: string;
  nextMultiplierBp: string;
  minePositions: number[] | null;
  payout: string;
  provablyFair: MinesActiveState['provablyFair'] | null;
  busy: boolean;
  error: string | null;
}

export const initialMinesState: MinesState = {
  status: 'idle',
  roundId: null,
  minesCount: 0,
  bet: '0',
  currency: 'THB',
  revealedTiles: [],
  multiplierBp: '0',
  nextMultiplierBp: '0',
  minePositions: null,
  payout: '0',
  provablyFair: null,
  busy: false,
  error: null,
};

type Action =
  | { type: 'busy'; value: boolean }
  | { type: 'opened'; state: MinesActiveState }
  | { type: 'confirming'; state: MinesUnconfirmedState }
  | { type: 'stake-not-taken'; error: string }
  | { type: 'reveal-pending'; tile: number }
  | { type: 'reveal-safe'; result: Extract<MinesRevealResult, { status: 'active' }> }
  | { type: 'reveal-failed'; tile: number; error: string }
  | { type: 'busted'; result: Extract<MinesRevealResult, { status: 'busted' }> }
  | { type: 'cashed'; result: MinesCashoutResult }
  | { type: 'error'; error: string }
  | { type: 'reset' };

export function minesReducer(state: MinesState, action: Action): MinesState {
  switch (action.type) {
    case 'busy':
      return { ...state, busy: action.value };
    case 'opened':
      return {
        ...state,
        status: 'active',
        roundId: action.state.roundId,
        minesCount: action.state.minesCount,
        bet: action.state.bet,
        currency: action.state.currency,
        revealedTiles: action.state.revealedTiles,
        multiplierBp: action.state.multiplierBp,
        nextMultiplierBp: action.state.nextMultiplierBp,
        minePositions: null,
        payout: '0',
        provablyFair: action.state.provablyFair,
        busy: false,
        error: null,
      };
    case 'confirming':
      return {
        ...initialMinesState,
        status: 'confirming',
        roundId: action.state.roundId,
        minesCount: action.state.minesCount,
        bet: action.state.bet,
        currency: action.state.currency,
      };
    case 'stake-not-taken':
      return { ...initialMinesState, error: action.error };
    case 'reveal-pending':
      return { ...state, revealedTiles: [...state.revealedTiles, action.tile], busy: true, error: null };
    case 'reveal-safe':
      return {
        ...state,
        revealedTiles: action.result.revealedTiles,
        multiplierBp: action.result.multiplierBp,
        nextMultiplierBp: action.result.nextMultiplierBp,
        busy: false,
      };
    case 'reveal-failed':
      return { ...state, revealedTiles: state.revealedTiles.filter((t) => t !== action.tile), busy: false, error: action.error };
    case 'busted':
      return {
        ...state,
        status: 'busted',
        revealedTiles: action.result.revealedTiles,
        minePositions: action.result.minePositions,
        multiplierBp: '0',
        payout: '0',
        busy: false,
      };
    case 'cashed':
      return {
        ...state,
        status: 'cashed',
        minePositions: action.result.minePositions,
        payout: action.result.payout,
        multiplierBp: action.result.multiplierBp,
        busy: false,
      };
    case 'error':
      return { ...state, busy: false, error: action.error };
    case 'reset':
      return { ...initialMinesState };
    default:
      return state;
  }
}

/**
 * Resolves a failed mines call to the i18n key + namespace that names the
 * reason. The key is the bare error code: the `errors` catalogue is flat, and
 * i18next's default keySeparator is '.', so a dotted `errors.<code>` form is
 * read as a PATH inside the namespace and renders the key itself on screen.
 */
export function minesErrorKey(err: unknown): { key: string; ns: 'errors' | 'originals' } {
  return err instanceof ApiError && err.code
    ? { key: err.code, ns: 'errors' }
    : { key: 'errorLoading', ns: 'originals' };
}

/**
 * Renders that key. `defaultValue` is what stops an uncatalogued code reaching
 * a player as the literal string "ORIG-004": i18next returns the KEY when it
 * cannot resolve one, and most backend codes have no catalogue entry. Same
 * guard, and same reasoning, as `mapApiError`.
 */
export function minesErrorText(err: unknown, t: Translator): string {
  const { key, ns } = minesErrorKey(err);
  return t(key, { ns, defaultValue: t('unknown', { ns: 'errors' }) });
}

export function useMinesRound({ enabled = true }: { enabled?: boolean } = {}) {
  const { t } = useTranslation('originals');
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(minesReducer, initialMinesState);
  const inFlight = useRef(false);

  const invalidateBalance = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: WALLET_QUERY_KEYS.balances });
    // The bet counts toward any active bonus turnover — refresh promo claims so
    // the Promo page isn't stale after a mines round.
    invalidateClaimState(queryClient);
  }, [queryClient]);

  // A live round is either a board to play or a bet still being confirmed.
  const showLive = useCallback((live: MinesLiveState) => {
    if (live.status === 'unconfirmed') dispatch({ type: 'confirming', state: live });
    else dispatch({ type: 'opened', state: live });
  }, []);

  // Resume on mount. A visitor with no session has no round to resume.
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void (async () => {
      try {
        const live = await getActiveMines();
        if (!cancelled && live) showLive(live);
      } catch {
        // no active round / fetch failed → stay idle
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, showLive]);

  // While the bet is being confirmed, ask `/active` until it opens (confirmed)
  // or is gone (the wallet refused it, so nothing was taken).
  useEffect(() => {
    if (state.status !== 'confirming') return;
    let cancelled = false;
    const timer = setInterval(() => {
      void (async () => {
        try {
          const live = await getActiveMines();
          if (cancelled) return;
          if (!live) {
            dispatch({ type: 'stake-not-taken', error: t('ORIG-021', { ns: 'errors' }) });
            invalidateBalance();
          } else if (live.status === 'active') {
            dispatch({ type: 'opened', state: live });
            invalidateBalance();
          }
        } catch {
          // keep asking on the next tick
        }
      })();
    }, CONFIRM_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [state.status, t, invalidateBalance]);

  const errorText = useCallback((err: unknown) => minesErrorText(err, t), [t]);

  const open = useCallback(
    async (betAmount: number, minesCount: number) => {
      if (inFlight.current) return;
      inFlight.current = true;
      dispatch({ type: 'busy', value: true });
      try {
        const live = await openMines({ betAmount, minesCount, idempotencyKey: crypto.randomUUID() });
        showLive(live);
        invalidateBalance();
      } catch (err) {
        dispatch({ type: 'error', error: errorText(err) });
      } finally {
        inFlight.current = false;
      }
    },
    [errorText, invalidateBalance, showLive],
  );

  const reveal = useCallback(
    async (tile: number) => {
      if (inFlight.current || !state.roundId || state.status !== 'active') return;
      if (state.revealedTiles.includes(tile)) return;
      inFlight.current = true;
      dispatch({ type: 'reveal-pending', tile });
      try {
        const result = await revealMines(state.roundId, tile);
        if (result.status === 'busted') {
          dispatch({ type: 'busted', result });
          invalidateBalance();
        } else if (result.status === 'cashed') {
          // Last safe tile → the server auto-settles the win; reflect it as cashed.
          dispatch({ type: 'cashed', result });
          invalidateBalance();
        } else {
          dispatch({ type: 'reveal-safe', result });
        }
      } catch (err) {
        dispatch({ type: 'reveal-failed', tile, error: errorText(err) });
        // refetch authoritative state after a mid-round failure
        try {
          const fresh = await getActiveMines();
          if (fresh) showLive(fresh);
        } catch {
          /* keep reverted state */
        }
      } finally {
        inFlight.current = false;
      }
    },
    [state.roundId, state.status, state.revealedTiles, errorText, invalidateBalance, showLive],
  );

  const cashout = useCallback(async () => {
    if (inFlight.current || !state.roundId || state.status !== 'active') return;
    inFlight.current = true;
    dispatch({ type: 'busy', value: true });
    try {
      const result = await cashoutMines(state.roundId);
      dispatch({ type: 'cashed', result });
      invalidateBalance();
    } catch (err) {
      dispatch({ type: 'error', error: errorText(err) });
    } finally {
      inFlight.current = false;
    }
  }, [state.roundId, state.status, errorText, invalidateBalance]);

  const reset = useCallback(() => dispatch({ type: 'reset' }), []);

  return { ...state, open, reveal, cashout, reset };
}
